import { useId, useLayoutEffect, useRef, useState } from 'react';
import ChartLegend from '../ChartLegend/ChartLegend';
import { pickTickIndices } from '../charts/chartMath';
import { CHART_COLORS, CHART_REGION_FILL } from '../charts/chartTheme';
import { useIndexHover, tooltipStyleAt } from '../charts/useIndexHover';
import { useChartSize } from '../charts/useChartSize';
import ChartTooltip from '../charts/ChartTooltip';
import { t } from '../../i18n/i18n';
import './CandlestickChart.scss';

/**
 * @typedef {{ time: string | number, open: number, high: number, low: number, close: number }} Candle
 */

const MAX_X_TICKS = 6;
const Y_DOMAIN_HEADROOM = 0.08;
const BODY_WIDTH_RATIO = 0.6;
const ENTRY_MARKER_WIDTH = 14;
const ENTRY_MARKER_HEIGHT = 8;
const EXIT_MARKER_RADIUS = 6;

const round2 = (n) => Math.round(n * 100) / 100;
const formatCandle = (d) =>
  `${d.time}  O ${round2(d.open)}  H ${round2(d.high)}  L ${round2(d.low)}  C ${round2(d.close)}`;

/**
 * OHLC candlestick chart with an optional shaded trade-region overlay,
 * dashed entry/exit boundary lines, and entry/exit point markers.
 *
 * @param {{
 *   data: Candle[],
 *   upColor?: string,
 *   downColor?: string,
 *   highlightRange?: { startIndex: number, endIndex: number, fillColor?: string },
 *   boundaryLines?: { index: number, color: string, label?: string }[],
 *   markers?: { index: number, type: 'entry' | 'exit', color?: string }[],
 *   showLegend?: boolean,
 *   showGridlines?: boolean,
 *   showXAxis?: boolean,
 *   showYAxis?: boolean,
 *   showTooltip?: boolean,
 *   tooltipFormatter?: (candle: Candle) => string,
 *   height?: number,
 *   className?: string,
 * }} props
 */
export default function CandlestickChart({
  data = [],
  upColor = CHART_COLORS.up,
  downColor = CHART_COLORS.down,
  highlightRange,
  boundaryLines = [],
  markers = [],
  showLegend = false,
  showGridlines = false,
  showXAxis = true,
  showYAxis = true,
  showTooltip = true,
  tooltipFormatter = formatCandle,
  height = 260,
  className = '',
}) {
  const clipId = useId();
  const tooltipRef = useRef(null);
  const { containerRef, w, scale, containerStyle } = useChartSize(height);
  const [tooltipWidth, setTooltipWidth] = useState(0);

  const n = data.length;
  const padLeft = showYAxis ? 46 : 10;
  const padRight = 10;
  const padTop = 14;
  const padBottom = showXAxis ? 26 : 10;
  const plotWidth = Math.max(w - padLeft - padRight, 1);
  const plotHeight = Math.max(height - padTop - padBottom, 1);
  const baselineY = padTop + plotHeight;

  const highs = n ? data.map((d) => d.high) : [1];
  const lows = n ? data.map((d) => d.low) : [0];
  let domainMin = Math.min(...lows);
  let domainMax = Math.max(...highs);
  if (domainMin === domainMax) {
    domainMin -= 1;
    domainMax += 1;
  }
  const span = domainMax - domainMin;
  domainMin -= span * Y_DOMAIN_HEADROOM;
  domainMax += span * Y_DOMAIN_HEADROOM;

  const slotWidth = plotWidth / Math.max(n, 1);
  const xAt = (i) => padLeft + slotWidth * (i + 0.5);
  const yAt = (value) =>
    padTop + (1 - (value - domainMin) / (domainMax - domainMin)) * plotHeight;

  const bodyWidth = Math.max(slotWidth * BODY_WIDTH_RATIO, 1);

  const highlightRect = highlightRange
    ? {
        x: xAt(highlightRange.startIndex) - slotWidth / 2,
        width:
          xAt(highlightRange.endIndex) + slotWidth / 2 - (xAt(highlightRange.startIndex) - slotWidth / 2),
        fill: highlightRange.fillColor || CHART_REGION_FILL,
      }
    : null;

  const yTicks =
    showYAxis || showGridlines
      ? Array.from({ length: 4 }, (_, i) => {
          const value = domainMin + (i / 3) * (domainMax - domainMin);
          return { value, y: yAt(value) };
        }).reverse()
      : [];

  const xTickIndices = showXAxis ? pickTickIndices(n, MAX_X_TICKS) : [];

  const legendItems = Array.from(
    new Map(
      markers.map((m) => [
        m.type,
        {
          color: m.color || (m.type === 'entry' ? CHART_COLORS.down : CHART_COLORS.info),
          label: m.type === 'entry' ? 'Entry' : 'Exit',
        },
      ])
    ).values()
  );

  const interactive = showTooltip && n > 0;
  const { hoverIndex, handlePointerMove, handlePointerLeave, handleKeyDown } = useIndexHover({
    n,
    padLeft,
    plotWidth,
    w,
  });
  const activeIndex = interactive ? hoverIndex : null;
  const activeCandle = activeIndex !== null ? data[activeIndex] : null;

  useLayoutEffect(() => {
    if (tooltipRef.current) setTooltipWidth(tooltipRef.current.offsetWidth);
  }, [activeIndex]);

  let tooltipStyle = null;
  if (activeCandle) {
    tooltipStyle = tooltipStyleAt(xAt(activeIndex), yAt(activeCandle.high) - 6, tooltipWidth, padLeft, padRight, w, scale);
  }

  const chartSvg = (
    <svg
      className="ac-candlestick-chart__svg"
      width="100%"
      height="100%"
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={t('Candlestick chart')}
    >
      <defs>
        <clipPath id={`${clipId}-plot`}>
          <rect x={padLeft} y={padTop} width={plotWidth} height={plotHeight} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId}-plot)`}>
        {showGridlines &&
            yTicks.map((tick) => (
              <line
                key={`grid-${tick.value}`}
                className="ac-candlestick-chart__gridline"
                x1={padLeft}
                x2={padLeft + plotWidth}
                y1={tick.y}
                y2={tick.y}
              />
            ))}

          {highlightRect && (
            <rect
              className="ac-candlestick-chart__region"
              x={highlightRect.x}
              y={padTop}
              width={highlightRect.width}
              height={plotHeight}
              fill={highlightRect.fill}
            />
          )}

          {data.map((d, i) => {
            const up = d.close >= d.open;
            const color = up ? upColor : downColor;
            const x = xAt(i);
            const bodyTop = yAt(Math.max(d.open, d.close));
            const bodyBottom = yAt(Math.min(d.open, d.close));
            return (
              <g key={i}>
                <line
                  className="ac-candlestick-chart__wick"
                  style={{ stroke: color }}
                  x1={x}
                  x2={x}
                  y1={yAt(d.high)}
                  y2={yAt(d.low)}
                />
                <rect
                  x={x - bodyWidth / 2}
                  y={bodyTop}
                  width={bodyWidth}
                  height={Math.max(bodyBottom - bodyTop, 1)}
                  fill={color}
                />
              </g>
            );
          })}

          {boundaryLines.map((line, i) => (
            <line
              key={i}
              className="ac-candlestick-chart__boundary"
              style={{ stroke: line.color }}
              x1={xAt(line.index)}
              x2={xAt(line.index)}
              y1={padTop}
              y2={baselineY}
            />
          ))}

          {markers.map((marker, i) => {
            const candle = data[marker.index];
            if (!candle) return null;
            const isEntry = marker.type === 'entry';
            const color = marker.color || (isEntry ? CHART_COLORS.down : CHART_COLORS.info);
            const x = xAt(marker.index);
            const bodyTop = yAt(Math.max(candle.open, candle.close));
            if (isEntry) {
              const halfWidth = ENTRY_MARKER_WIDTH / 2;
              return (
                <path
                  key={i}
                  className="ac-candlestick-chart__marker-entry"
                  d={`M${x - halfWidth},${bodyTop - ENTRY_MARKER_HEIGHT} L${x + halfWidth},${bodyTop - ENTRY_MARKER_HEIGHT} L${x},${bodyTop} Z`}
                  fill={color}
                />
              );
            }
            return (
              <circle
                key={i}
                className="ac-candlestick-chart__marker-exit"
                style={{ stroke: color }}
                cx={x}
                cy={bodyTop}
                r={EXIT_MARKER_RADIUS}
              />
            );
          })}

          {activeCandle && (
            <line
              className="ac-candlestick-chart__guide"
              x1={xAt(activeIndex)}
              x2={xAt(activeIndex)}
              y1={padTop}
              y2={baselineY}
            />
          )}
        </g>

        {showYAxis &&
          yTicks.map((tick) => (
            <text
              key={`y-${tick.value}`}
              className="ac-candlestick-chart__y-label"
              x={padLeft - 10}
              y={tick.y}
            >
              {tick.value.toFixed(2)}
            </text>
          ))}

        {showXAxis &&
          xTickIndices.map((i) => (
            <text
              key={`x-${i}`}
              className="ac-candlestick-chart__x-label"
              x={xAt(i)}
              y={height - padBottom + 18}
            >
              {data[i].time}
            </text>
          ))}
    </svg>
  );

  return (
    <div
      ref={containerRef}
      className={`ac-candlestick-chart ${className}`.trim()}
      style={containerStyle}
    >
      {interactive ? (
        <div
          className="ac-candlestick-chart__interactive"
          role="group"
          aria-label={t('Candlestick chart')}
          tabIndex={0}
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onKeyDown={handleKeyDown}
        >
          {chartSvg}
        </div>
      ) : (
        chartSvg
      )}

      {tooltipStyle && (
        <ChartTooltip tooltipRef={tooltipRef} style={tooltipStyle}>
          {tooltipFormatter(activeCandle)}
        </ChartTooltip>
      )}

      {showLegend && <ChartLegend items={legendItems} className="ac-candlestick-chart__legend" />}
    </div>
  );
}
